import React, { useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    ClickAwayListener,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Popper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { CircleX, Search } from 'lucide-react';

import { countries, emailRegex } from '../../utils/constants';
import InputLabel from '../common/form/InputLabel';
import CountryCodeSelect from '../common/CountryCodeSelect';
import {
    getFilteredRenterOptions,
    isValidPhoneForCountry
} from 'utils/helpers/rental-form-modal.helper';

const inputSx = {
    '& .MuiInputBase-root': {
        height: 32,
        fontSize: 13,
        bgcolor: 'white',
        borderRadius: '4px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d9d9d9'
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#b0b0b0'
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#33c78f',
            borderWidth: '1px'
        }
    },
    '& input': {
        py: '4px',
        px: '12px',
        fontSize: 13
    },
    '& input::placeholder': {
        color: '#8c8c8c',
        opacity: 1
    }
};

const selectSx = {
    height: 32,
    minWidth: 92,
    bgcolor: 'white',
    borderRadius: '4px',
    fontSize: 13,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#d9d9d9'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#b0b0b0'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#33c78f'
    },
    '& .MuiSelect-select': {
        py: '4px',
        pl: '8px',
        pr: '26px !important',
        display: 'flex',
        alignItems: 'center'
    }
};

const capitalizeNamePart = value =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/(^|\s|-|')\S/g, char => char.toUpperCase());

const formatPersonName = person => {
    return [person?.first_name, person?.last_name]
        .map(capitalizeNamePart)
        .filter(Boolean)
        .join(' ');
};

const getCountryOption = countryValue =>
    countries.find(country => country.value === countryValue) || countries[0];

const getSmartInputValue = user => {
    if (user?.email) return String(user.email);
    if (user?.phone?.number) return String(user.phone.number);

    return [user?.first_name, user?.last_name].filter(Boolean).join(' ');
};

const getEmptyPerson = () => ({
    phone: {
        number: '',
        code: '1',
        country: 'US'
    },
    first_name: '',
    last_name: '',
    email: '',
    minor: false,
    renterFound: undefined
});

const UserForm = ({
    user,
    fieldPrefix,
    setFieldValue,
    errors,
    touched,
    userFound,
    onSearch,
    searchLoading,
    loading,
    disabled,
    isGuest = false,
    renterOptions = [],
    isEditMode = false
}) => {
    const getError = field => {
        if (field === 'phone.number') {
            return touched?.phone?.number && errors?.phone?.number
                ? errors.phone.number
                : '';
        }

        return touched?.[field] && errors?.[field] ? errors[field] : '';
    };

    const isDisabled = disabled || loading;
    const [searchedField, setSearchedField] = useState(null); // 'email' | 'phone' | null
    // Identity fields (name/email/phone) are locked once who this is is no longer up for
    // grabs: an *existing* renter/guest on a saved reservation (isEditMode, and — for
    // guests — already has an id from the backend), or a search that already matched
    // someone. A guest just pushed onto an editable reservation (e.g. adding one to an
    // upcoming reservation) has no id yet — it's a fresh add, not an edit, so it stays
    // unlocked exactly like create mode until a search actually matches it. Changing a
    // locked identity has to go through "Search again" in create mode, or a separate
    // lookup flow when editing — never by typing over the field directly.
    const isExistingIdentity = !isGuest || Boolean(user.id);
    const identityLocked = (isEditMode && isExistingIdentity) || userFound === true;
    // A search that came up empty still fixes whichever contact value was searched —
    // only the other identity fields (name, and the untried contact method) stay open.
    const searchedByEmail = userFound === false && searchedField === 'email';
    const searchedByPhone = userFound === false && searchedField === 'phone';
    const searchAgainDisabled = loading || disabled;
    const searchValue = getSmartInputValue(user);
    const hasSearchValue = Boolean(String(searchValue).trim());
    const searchLooksLikeEmail = String(searchValue).includes('@');
    const searchLooksLikePhone =
        !searchLooksLikeEmail && !/[a-zA-Z]/.test(String(searchValue));
    const searchPhoneInvalid =
        searchLooksLikePhone &&
        Boolean(String(user.phone?.number || '').trim()) &&
        !isValidPhoneForCountry(
            user.phone?.code,
            user.phone?.number,
            user.phone?.country
        );
    const searchEmailInvalid =
        searchLooksLikeEmail && !emailRegex.test(String(searchValue).trim());
    // The lookup only makes sense with an email or a phone. A bare name (no
    // returning renter/guest picked from the suggestions) is not searchable.
    const searchValueIsContact =
        searchLooksLikeEmail ||
        Boolean(String(user.phone?.number || '').trim());

    const filteredRenterOptions = getFilteredRenterOptions({
        renterOptions,
        searchValue,
        isGuest
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const [searchCountry, setSearchCountry] = useState(null);
    const [searchTouched, setSearchTouched] = useState(false);

    const suggestionsOpen =
        Boolean(anchorEl) && userFound === undefined && filteredRenterOptions.length > 0;

    const handleSelectRenter = renter => {
        setFieldValue(`${fieldPrefix}.first_name`, renter.first_name || '');
        setFieldValue(`${fieldPrefix}.last_name`, renter.last_name || '');
        setFieldValue(`${fieldPrefix}.email`, renter.email || '');
        setFieldValue(`${fieldPrefix}.phone.number`, String(renter.phone?.number || ''));
        setFieldValue(`${fieldPrefix}.phone.code`, String(renter.phone?.code || '1'));
        setFieldValue(`${fieldPrefix}.phone.country`, renter.phone?.country || 'US');
        setFieldValue(`${fieldPrefix}.renterFound`, true);
    };
    const resetPerson = () => {
        setFieldValue(fieldPrefix, getEmptyPerson());
        setSearchCountry(null);
        setSearchTouched(false);
        setSearchedField(null);
    };

    const handleCountryChange = value => {
        const selectedCountry = getCountryOption(value);

        setFieldValue(`${fieldPrefix}.phone.code`, String(selectedCountry.phoneCode));
        setFieldValue(`${fieldPrefix}.phone.country`, selectedCountry.value);
    };

    const handleSmartChange = value => {
        if (userFound !== undefined) {
            setFieldValue(`${fieldPrefix}.renterFound`, undefined);
        }

        if (value.includes('@')) {
            setFieldValue(`${fieldPrefix}.email`, value);
            setFieldValue(`${fieldPrefix}.phone.number`, '');
            setFieldValue(`${fieldPrefix}.first_name`, '');
            setFieldValue(`${fieldPrefix}.last_name`, '');
            return;
        }

        if (/[a-zA-Z]/.test(value)) {
            const parts = value.trimStart().split(/\s+/);
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ');

            setFieldValue(`${fieldPrefix}.first_name`, firstName);
            setFieldValue(`${fieldPrefix}.last_name`, lastName);
            setFieldValue(`${fieldPrefix}.email`, '');
            setFieldValue(`${fieldPrefix}.phone.number`, '');
            return;
        }

        setFieldValue(`${fieldPrefix}.phone.number`, value.replace(/\D/g, ''));
        setFieldValue(`${fieldPrefix}.email`, '');
        setFieldValue(`${fieldPrefix}.first_name`, '');
        setFieldValue(`${fieldPrefix}.last_name`, '');
    };

    return (
        <Box>
            {!isGuest && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        mb: 2
                    }}
                >
                    <Box>
                        <InputLabel label="Who is Renting?" fontWeight={450} />

                        <Typography
                            sx={{
                                fontSize: 13,
                                color: '#8c8c8c',
                                lineHeight: '22px'
                            }}
                        >
                            Search for a returning renter or add a new one.
                        </Typography>
                    </Box>

                    {userFound !== undefined && !isEditMode && (
                        <Button
                            startIcon={<Search size={12} />}
                            onClick={resetPerson}
                            disabled={searchAgainDisabled}
                            sx={{
                                textTransform: 'none',
                                fontSize: 13,
                                fontWeight: 400,
                                color: '#009966',
                                p: 0,
                                minWidth: 0,
                                mt: '2px',
                                '&:hover': {
                                    bgcolor: 'transparent',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Search again
                        </Button>
                    )}
                </Box>
            )}

            {userFound === undefined ? (
                <Box sx={{ position: 'relative' }}>
                    <InputLabel
                        label="Enter their phone number or email"
                        required
                    />

                    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                            <Stack
                                direction="row"
                                spacing="10px"
                                alignItems="flex-start"
                            >
                                {searchLooksLikePhone && (
                                    <CountryCodeSelect
                                        value={searchCountry}
                                        defaultRegion={user.phone?.country || 'US'}
                                        onChange={selectedCountry => {
                                            if (!selectedCountry) return;

                                            setSearchCountry(selectedCountry);
                                            setFieldValue(
                                                `${fieldPrefix}.phone.code`,
                                                String(selectedCountry.code).replace(
                                                    /\D/g,
                                                    ''
                                                )
                                            );
                                            setFieldValue(
                                                `${fieldPrefix}.phone.country`,
                                                selectedCountry.region
                                            );
                                        }}
                                        label=""
                                        size="small"
                                        disabled={isDisabled}
                                        sx={{
                                            width: { xs: 88, sm: 132 },
                                            flex: '0 0 auto'
                                        }}
                                    />
                                )}

                                <TextField
                                    fullWidth
                                    placeholder="Phone number or email..."
                                    value={searchValue}
                                    onFocus={event => setAnchorEl(event.currentTarget)}
                                    onChange={event => {
                                        setAnchorEl(event.currentTarget);
                                        handleSmartChange(event.target.value);
                                    }}
                                    onBlur={() => setSearchTouched(true)}
                                    disabled={isDisabled}
                                    error={
                                        searchTouched &&
                                        (searchPhoneInvalid || searchEmailInvalid)
                                    }
                                    helperText={
                                        searchTouched && searchPhoneInvalid
                                            ? 'Phone number needs to be valid'
                                            : searchTouched && searchEmailInvalid
                                            ? 'Invalid email'
                                            : ''
                                    }
                                    sx={inputSx}
                                    InputProps={{
                                        endAdornment: hasSearchValue ? (
                                            <InputAdornment position="end">
                                                <CircleX
                                                    size={16}
                                                    color="#33C78F"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={resetPerson}
                                                />
                                            </InputAdornment>
                                        ) : null
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Button
                            variant="contained"
                            onClick={() => {
                                setSearchedField(
                                    searchLooksLikeEmail
                                        ? 'email'
                                        : searchLooksLikePhone
                                        ? 'phone'
                                        : null
                                );
                                onSearch();
                            }}
                            disabled={
                                !hasSearchValue ||
                                isDisabled ||
                                !searchValueIsContact ||
                                searchPhoneInvalid ||
                                searchEmailInvalid
                            }
                            sx={{
                                height: 32,
                                minWidth: { xs: 52, sm: 72 },
                                px: { xs: 1.5, sm: 2 },
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontSize: 13,
                                boxShadow: 'none',
                                bgcolor: hasSearchValue ? '#33c78f' : '#f5f5f5',
                                color: hasSearchValue ? 'white' : '#d9d9d9',
                                '&:hover': {
                                    bgcolor: hasSearchValue ? '#28b07d' : '#f5f5f5',
                                    boxShadow: 'none'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#f5f5f5',
                                    color: '#d9d9d9'
                                }
                            }}
                        >
                            {searchLoading ? '...' : 'Add'}
                        </Button>
                    </Box>

                    <Popper
                        open={suggestionsOpen}
                        anchorEl={anchorEl}
                        placement="bottom-start"
                        disablePortal={false}
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 4]
                                }
                            }
                        ]}
                        sx={{
                            zIndex: 2000,
                            width: anchorEl?.offsetWidth || 'auto'
                        }}
                    >
                        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                            <Paper
                                elevation={3}
                                sx={{
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    maxHeight: 260,
                                    overflowY: 'auto'
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        bgcolor: '#fafafa',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: '#8c8c8c'
                                        }}
                                    >
                                        {isGuest
                                            ? 'Returning guest?'
                                            : 'Returning renter?'}
                                    </Typography>
                                </Box>

                                {filteredRenterOptions.map(option => {
                                    const fullName = formatPersonName(option);

                                    return (
                                        <Box
                                            key={`${option.property_id}:${option.id}`}
                                            onClick={() => {
                                                handleSelectRenter(option);
                                                setAnchorEl(null);
                                            }}
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f5f5f5',
                                                '&:hover': {
                                                    bgcolor: '#eefaf5'
                                                }
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    fontWeight: 500,
                                                    color: '#212322'
                                                }}
                                            >
                                                {fullName ||
                                                    option.email ||
                                                    option.phone?.number}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontSize: 12,
                                                    color: '#8c8c8c',
                                                    mt: '2px'
                                                }}
                                            >
                                                {[
                                                    option.phone?.number
                                                        ? `+${option.phone.code}${option.phone.number}`
                                                        : null,
                                                    option.email
                                                ]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </Box>
            ) : (
                <Grid container spacing="15px">
                    <Grid item xs={12} sm={6}>
                        <InputLabel label="First Name" required />

                        <TextField
                            fullWidth
                            value={user.first_name || ''}
                            onChange={event =>
                                setFieldValue(
                                    `${fieldPrefix}.first_name`,
                                    event.target.value
                                )
                            }
                            disabled={isDisabled || identityLocked}
                            error={Boolean(getError('first_name'))}
                            helperText={getError('first_name')}
                            placeholder="First name"
                            sx={inputSx}
                            inputProps={{
                                style: { textTransform: 'capitalize' }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <InputLabel label="Last Name" required />

                        <TextField
                            fullWidth
                            value={user.last_name || ''}
                            onChange={event =>
                                setFieldValue(
                                    `${fieldPrefix}.last_name`,
                                    event.target.value
                                )
                            }
                            disabled={isDisabled || identityLocked}
                            error={Boolean(getError('last_name'))}
                            helperText={getError('last_name')}
                            placeholder="Last name"
                            sx={inputSx}
                            inputProps={{
                                style: { textTransform: 'capitalize' }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <InputLabel label="Phone Number" required />

                        <Stack direction="row" spacing="10px">
                            <Select
                                value={user.phone?.country || 'US'}
                                disabled={isDisabled || identityLocked || searchedByPhone}
                                onChange={event =>
                                    handleCountryChange(event.target.value)
                                }
                                sx={selectSx}
                                renderValue={value => {
                                    const selectedCountry = getCountryOption(value);

                                    return `${selectedCountry.flagIcon} +${selectedCountry.phoneCode}`;
                                }}
                            >
                                {countries.map(country => (
                                    <MenuItem key={country.value} value={country.value}>
                                        {country.flagIcon} +{country.phoneCode}
                                    </MenuItem>
                                ))}
                            </Select>

                            <TextField
                                fullWidth
                                placeholder="Phone number"
                                inputMode="numeric"
                                value={user.phone?.number || ''}
                                onChange={event =>
                                    setFieldValue(
                                        `${fieldPrefix}.phone.number`,
                                        event.target.value.replace(/\D/g, '')
                                    )
                                }
                                disabled={isDisabled || identityLocked || searchedByPhone}
                                error={Boolean(getError('phone.number'))}
                                helperText={getError('phone.number')}
                                sx={inputSx}
                            />
                        </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <InputLabel label="Email Address" optional />

                        <TextField
                            fullWidth
                            value={user.email || ''}
                            onChange={event =>
                                setFieldValue(`${fieldPrefix}.email`, event.target.value)
                            }
                            disabled={isDisabled || identityLocked || searchedByEmail}
                            error={Boolean(getError('email'))}
                            helperText={getError('email')}
                            placeholder="email address"
                            sx={inputSx}
                        />
                    </Grid>

                    {isGuest && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                sx={{ ml: 0 }}
                                control={
                                    <Checkbox
                                        checked={Boolean(user.minor)}
                                        onChange={event =>
                                            setFieldValue(
                                                `${fieldPrefix}.minor`,
                                                event.target.checked
                                            )
                                        }
                                        disabled={isDisabled}
                                    />
                                }
                                label="Minor"
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default UserForm;
